import React from "react"
import { Box, Chip, IconButton, Paper, Typography } from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import {
  buildFlightSearchUrl,
  formatCardDate,
  relativeDayLabel,
} from "utils/flightLogic"

// One-line clamp. Long names and notes have to give up width rather than push
// the card taller -- vertical space is the scarce resource on a phone.
const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

function FlightCard({ flight, today, past = false, onMenu }) {
  const label = past ? null : relativeDayLabel(flight.date, today)
  const imminent = Boolean(label)
  const carrier = flight.airlineCode || flight.airline
  const route =
    flight.origin && flight.destination
      ? `${flight.origin} → ${flight.destination}`
      : null

  return (
    <Paper
      component="a"
      href={buildFlightSearchUrl(flight)}
      target="_blank"
      rel="noopener noreferrer"
      elevation={0}
      sx={{
        display: "block",
        // Keeps the nowrap rows ellipsizing instead of widening the card if it
        // is ever placed in a container that sizes to content.
        maxWidth: "100%",
        textDecoration: "none",
        color: "inherit",
        px: past ? { xs: 1.5, sm: 1.75 } : { xs: 1.75, sm: 2.5 },
        py: past ? { xs: 1.25, sm: 1.5 } : { xs: 1.5, sm: 2 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: imminent ? "primary.main" : "rgba(255,255,255,0.10)",
        background: imminent
          ? "linear-gradient(135deg, rgba(56,132,255,0.22), rgba(20,24,34,0.95))"
          : "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(20,24,34,0.92))",
        boxShadow: imminent ? "0 0 24px rgba(56,132,255,0.35)" : "none",
        opacity: past ? 0.6 : 1,
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:active": { transform: "scale(0.985)" },
        "@media (hover: hover)": {
          "&:hover": { boxShadow: "0 6px 28px rgba(0,0,0,0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {/* color="inherit" is load-bearing: _style.scss colors bare h1-h6
            elements dark navy, which the MuiTypography-h6 class does not
            override, leaving the name invisible on the dark card. */}
        <Typography
          variant={past ? "body1" : "h6"}
          color="inherit"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: past ? undefined : { xs: "1.05rem", sm: "1.25rem" },
            minWidth: 0,
            flexGrow: 1,
            ...ellipsis,
          }}
        >
          {flight.who}
        </Typography>

        {label && (
          <Chip
            size="small"
            color="primary"
            label={label}
            sx={{ flexShrink: 0 }}
          />
        )}

        {/* Pulled into the card's padding so the 44px target costs no layout
            width beyond the icon itself. */}
        <IconButton
          aria-label={`Options for ${flight.who} ${carrier} ${flight.flightNumber}`}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onMenu(flight)
          }}
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            my: -1.25,
            mr: -1,
            color: "inherit",
            opacity: 0.7,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mt: 0.5,
          minWidth: 0,
        }}
      >
        <FlightTakeoffIcon
          sx={{
            fontSize: 16,
            opacity: 0.7,
            flexShrink: 0,
            alignSelf: "center",
          }}
        />
        <Typography
          variant="body2"
          sx={{ opacity: 0.85, minWidth: 0, flexGrow: 1, ...ellipsis }}
        >
          {carrier} {flight.flightNumber}
          {route ? ` · ${route}` : ""}
        </Typography>
        <Typography
          variant="body2"
          sx={{ opacity: 0.7, flexShrink: 0, fontWeight: 600 }}
        >
          {formatCardDate(flight.date)}
        </Typography>
      </Box>

      {flight.note && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.25, opacity: 0.55, ...ellipsis }}
        >
          {flight.note}
        </Typography>
      )}
    </Paper>
  )
}

export default FlightCard
